import type { ReactElement } from 'react'
import { useState } from 'react'
import { Card, CardHead } from '~/components/ui/Card'
import { Button, DashedButton } from '~/components/ui/Controls'
import { StatusPill } from '~/components/ui/Status'
import { regimeLabel, useWarrants } from '~/state/warrants'
import type { WarrantTaxRateDraft } from '~/state/warrant-tax'
import {
  draftFromWarrantVersion,
  nextWarrantDraftAfter,
  refuseWarrantTaxRateDrafts,
  regimesIn,
  sortWarrantDrafts,
  toWarrantTaxRateVersion,
  useSaveWarrantTaxRates,
  useWarrantTaxRates,
} from '~/state/warrant-tax'

const CSS = `
.nw-warrant-tax p { margin: 0 0 var(--nw-space-3); }
.nw-warrant-tax .nw-lede { color: var(--nw-body-ink); font-size: var(--nw-text-cell); max-width: 70ch; }
.nw-warrant-tax ul {
  border: var(--nw-hairline-width) solid var(--nw-hairline); border-radius: var(--nw-radius-md);
  list-style: none; margin: var(--nw-space-4) 0 var(--nw-space-3); padding: 0;
}
.nw-warrant-tax li { border-bottom: var(--nw-hairline-width) solid var(--nw-row-rule); padding: var(--nw-space-3); }
.nw-warrant-tax li:last-child { border-bottom: 0; }
.nw-warrant-tax .nw-version-head { align-items: center; display: flex; flex-wrap: wrap; gap: var(--nw-space-2) var(--nw-space-3); }
.nw-warrant-tax .nw-version-head .nw-btn { margin-left: auto; }
.nw-warrant-tax .nw-fields { display: flex; flex-wrap: wrap; gap: var(--nw-space-3); margin-top: var(--nw-space-3); }
.nw-warrant-tax label { display: block; }
.nw-warrant-tax label > span { color: var(--nw-muted); display: block; font-size: var(--nw-text-meta); font-weight: var(--nw-weight-strong); letter-spacing: var(--nw-track-label); text-transform: uppercase; }
.nw-warrant-tax .nw-unit { align-items: center; display: flex; gap: var(--nw-space-1); }
.nw-warrant-tax .nw-unit span[aria-hidden] { color: var(--nw-muted); font-size: var(--nw-text-cell); letter-spacing: 0; text-transform: none; }
.nw-warrant-tax input[type='number'] { text-align: right; width: 96px; }
.nw-warrant-tax input[type='date'] { width: 160px; }
.nw-warrant-tax .nw-decide { align-items: center; display: flex; flex-wrap: wrap; gap: var(--nw-space-3); }
.nw-warrant-tax .nw-error { color: var(--nw-critical); font-size: var(--nw-text-cell); margin: 0; }
.nw-warrant-tax .nw-unsaved { color: var(--nw-body-ink); font-size: var(--nw-text-chip); margin: 0; }
.nw-warrant-tax .nw-disclaimer { color: var(--nw-muted); font-size: var(--nw-text-chip); max-width: 70ch; }
`

/**
 * What exercising a warrant is charged at, and the only place a household can
 * say so.
 *
 * Every household is created holding the same Danish rates — §28 taxes the
 * spread as personal income, §7P defers it to the sale — because the product
 * was first written against that regime and no rates at all is worse than rates
 * from the wrong country: with none, the cash an exercise costs is left blank
 * on every grant. They were nonetheless unreadable and unchangeable from
 * anywhere, so a household elsewhere was shown a Danish answer with nothing on
 * the page admitting it was one.
 *
 * On Settings, beside the general rates, because three pages already send
 * people there by name for the others and a household looking for what its
 * figures were charged at should not have to learn two places.
 *
 * Withheld until the household holds a grant, the way the card above it is
 * withheld until there is a company. A card about exercising a warrant, on the
 * page every household opens, for a capability most of them never take up, is
 * the fault this card exists to fix wearing the other hat.
 *
 * The gate has a door outside it: a first grant is entered through
 * `/manual` → Add holding → A warrant grant, which is ungated, so no household
 * is shut out of rates it has figures for. That door has to open for a
 * household that is not taxed under either Danish statute too, which is why the
 * grant form names the regime rather than this card doing it — a household
 * taxed elsewhere would otherwise have to file its first grant under a statute
 * that does not apply to it before it could reach the card that says so. What
 * this card does is price what the grants name. Nothing is lost by waiting
 * either — the rates have defaults, and until there is a grant there is no
 * figure they change.
 */
export function WarrantTaxRatesPanel(): ReactElement | null {
  const rates = useWarrantTaxRates()
  const save = useSaveWarrantTaxRates()
  const grants = useWarrants()
  const [drafts, setDrafts] = useState<WarrantTaxRateDraft[] | null>(null)

  // Absent rather than false until the answer arrives: a card that appears and
  // then withdraws reads as a fault.
  const held = (grants.data?.grants.length ?? 0) + (grants.data?.unvaluable?.length ?? 0)
  if (grants.data === undefined || held === 0) return null

  const versions = rates.data?.versions ?? []
  const stored = versions.map(draftFromWarrantVersion)
  const shown = drafts ?? stored
  const regimes = regimesIn(rates.data?.regimes ?? [], versions)
  const inForce = rates.data?.effective?.effectiveFrom
  const refusal = refuseWarrantTaxRateDrafts(shown)
  const edited = drafts !== null && JSON.stringify(drafts) !== JSON.stringify(stored)
  const busy = rates.isPending || save.isPending

  const edit = (index: number, next: WarrantTaxRateDraft): void =>
    setDrafts(shown.map((draft, at) => (at === index ? next : draft)))

  const commit = (): void => {
    if (refusal !== undefined) return
    save.mutate(sortWarrantDrafts(shown).map(toWarrantTaxRateVersion), {
      onSuccess: () => setDrafts(null),
    })
  }

  return (
    <Card className="nw-warrant-tax">
      <style>{CSS}</style>
      <CardHead
        id="nw-warrant-tax-heading"
        title="Tax on exercising a warrant"
        lede="These are this household's rates, not a rule of the world. A household is created holding Danish ones because those are the rates this was first built against; change them to whatever the household is actually charged, and the cash each exercise costs follows."
      />

      {rates.error && <p className="nw-error">Could not read the rates: {rates.error.message}</p>}
      {save.error && <p className="nw-error">Could not save: {save.error.message}</p>}

      <p className="nw-lede">
        A version applies from its date until the next one starts, and a rate is charged on the
        spread at exercise. Nothing applies before the earliest date below: a grant exercised
        before it has no rate at all, and the cash it costs is left blank rather than charged at
        nothing. A regime left empty is one this household has not said, which is read the same
        way.
      </p>

      {!rates.isPending && shown.length === 0 && (
        <p className="nw-lede">
          No exercise rates are configured. Nothing is charged on any exercise anywhere, so Cash to
          realise and Left after both are blank rather than wrong.
        </p>
      )}

      {shown.length > 0 && (
        <ul>
          {shown.map((draft, index) => (
            <VersionRow
              key={index}
              draft={draft}
              regimes={regimes}
              inForce={draft.effectiveFrom !== '' && draft.effectiveFrom === inForce}
              disabled={busy}
              onChange={(next) => edit(index, next)}
              onRemove={() => setDrafts(shown.filter((_, at) => at !== index))}
            />
          ))}
        </ul>
      )}

      <DashedButton
        disabled={busy}
        onClick={() =>
          setDrafts([...shown, nextWarrantDraftAfter(shown[shown.length - 1], regimes)])
        }
      >
        Add a version
      </DashedButton>

      <div className="nw-decide" style={{ marginTop: 'var(--nw-space-3)' }}>
        <Button kind="primary" disabled={busy || !edited || refusal !== undefined} onClick={commit}>
          {save.isPending ? 'Saving…' : 'Save rates'}
        </Button>
        {refusal !== undefined && <p className="nw-error">{refusal}</p>}
        {refusal === undefined && edited && (
          <p className="nw-unsaved">Not saved yet — no figure has moved.</p>
        )}
      </div>

      {rates.data !== undefined && <p className="nw-disclaimer">{rates.data.disclaimer}</p>}
    </Card>
  )
}

function VersionRow({
  draft,
  regimes,
  inForce,
  disabled,
  onChange,
  onRemove,
}: {
  draft: WarrantTaxRateDraft
  regimes: readonly string[]
  inForce: boolean
  disabled: boolean
  onChange: (next: WarrantTaxRateDraft) => void
  onRemove: () => void
}): ReactElement {
  const named = draft.effectiveFrom === '' ? 'a new version' : draft.effectiveFrom

  return (
    <li>
      <div className="nw-version-head">
        <label>
          <span>In force from</span>
          <input
            type="date"
            value={draft.effectiveFrom}
            disabled={disabled}
            aria-label={`Date the exercise rates of ${named} take effect`}
            onChange={(event) => onChange({ ...draft, effectiveFrom: event.target.value })}
          />
        </label>
        {inForce && <StatusPill tone="live" label="In force today" />}
        <Button disabled={disabled} onClick={onRemove}>
          Remove
        </Button>
      </div>

      <div className="nw-fields">
        {regimes.map((regime) => (
          <Percent
            key={regime}
            label={regimeLabel(regime)}
            of={named}
            value={draft.exercisePercent[regime] ?? ''}
            disabled={disabled}
            onChange={(percent) =>
              onChange({
                ...draft,
                exercisePercent: { ...draft.exercisePercent, [regime]: percent },
              })
            }
          />
        ))}
      </div>
    </li>
  )
}

function Percent({
  label,
  of,
  value,
  disabled,
  onChange,
}: {
  label: string
  of: string
  value: string
  disabled: boolean
  onChange: (value: string) => void
}): ReactElement {
  return (
    <label>
      <span>{label}</span>
      <div className="nw-unit">
        <input
          type="number"
          min={0}
          max={100}
          step={0.1}
          value={value}
          disabled={disabled}
          aria-label={`${label} rate from ${of}, in percent`}
          onChange={(event) => onChange(event.target.value)}
        />
        <span aria-hidden="true">%</span>
      </div>
    </label>
  )
}
